import { useEffect, useState } from 'react'
import { useT } from '@/lib/i18n'

const STORAGE_KEY = 'cookie_consent'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

function loadGtag(measurementId: string): void {
  if (window.gtag) return // already loaded (e.g. StrictMode double-effect)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)
}

interface Props {
  measurementId?: string
}

/**
 * GDPR-style consent gate for GA4. Nothing loads until the visitor
 * explicitly accepts — no measurementId, no banner, no tracking.
 * The choice is remembered in localStorage so returning visitors
 * aren't asked again either way.
 */
export function ConsentBanner({ measurementId }: Props) {
  const T = useT('common')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!measurementId) return
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'accepted') {
      loadGtag(measurementId)
    } else if (stored !== 'declined') {
      setVisible(true) // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [measurementId])

  if (!visible || !measurementId) return null

  function accept() {
    localStorage.setItem(STORAGE_KEY, 'accepted')
    if (measurementId) loadGtag(measurementId)
    setVisible(false)
  }

  function decline() {
    localStorage.setItem(STORAGE_KEY, 'declined')
    setVisible(false)
  }

  return (
    <div className="app-bottom-fixed fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur">
      <div className="mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-3">
        <p className="min-w-0 flex-1 text-xs text-gray-600">{T.consentText}</p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={decline}
            className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600"
          >
            {T.consentDecline}
          </button>
          <button
            type="button"
            onClick={accept}
            className="rounded-xl bg-brand-600 px-3 py-1.5 text-xs font-medium text-white"
          >
            {T.consentAccept}
          </button>
        </div>
      </div>
    </div>
  )
}
